import { StarFilled, StarTwoTone, FileTextOutlined, AppstoreOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Card, Col, Empty, Input, message, Modal, Pagination, Row, Typography, Button, Space, List, Avatar } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loading } from '../../components/Loading';
import { TreeSelectLocationUser } from '../../components/TreeSelectLocation';
import { TreeSelectTopic } from '../../components/TreeSelectTopic';
import Api, { Api_v2, exceptionNotificationAPI } from '../../services/api';
import { parseFormListToTable } from './helper';

const { confirm } = Modal;
const { Text, Title } = Typography;

const Forms = ({ user }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState('list'); // 'cards' ou 'list'
    const [filterRequest, setFilterRequest] = useState({
        pageNumber: 1,
        pageSize: 12,
        searchText: '',
        topicId: null,
    });
    const [locationId, setLocationId] = useState(null);
    const [paginationConfig, setPaginationConfig] = useState({ current: 1, total: 0 });
    const [listData, setListData] = useState([]);
    const [loading, setLoading] = useState(false);

    const getFormListData = useCallback(() => {
        setLoading(true);

        const locations = locationId ? [locationId] : [user?.locationId];
        const requestPayload = {
            ...filterRequest,
            locationIds: locations,
        };

        Api_v2.Form.getAdhocs(locations, requestPayload)
            .then(response => {
                const data = response.data;
                if (data && data.response) {
                    const parsed = parseFormListToTable(data);
                    setListData(parsed);
                    setPaginationConfig({
                        current: filterRequest.pageNumber,
                        total: data.response.rowCount,
                    });
                }
            })
            .catch(error => {
                exceptionNotificationAPI(error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [filterRequest, locationId, user?.locationId]);

    useEffect(() => {
        getFormListData();
    }, [getFormListData]);

    const handleFavoriteForm = item => {
        confirm({
            title: item.bookmark ? 'Remover favorito?' : 'Adicionar aos favoritos?',
            content: `Formulário: ${item.title}`,
            okText: 'Sim',
            cancelText: 'Não',
            onOk: async () => {
                try {
                    await Api.post(`/Form/favorite?formId=${item.id}`);
                    message.success('Alteração salva com sucesso');
                    getFormListData();
                } catch (err) {
                    message.error('Erro ao favoritar');
                }
            },
        });
    };

    const onSearchText = value => {
        setFilterRequest(prev => ({ ...prev, pageNumber: 1, searchText: value }));
    };

    const onTopicChange = value => {
        setFilterRequest(prev => ({ ...prev, pageNumber: 1, topicId: value }));
    };

    const onLocationChange = value => {
        setLocationId(value);
        setFilterRequest(prev => ({ ...prev, pageNumber: 1 }));
    };

    const onPageChange = page => {
        setPaginationConfig(prev => ({ ...prev, current: page }));
        setFilterRequest(prev => ({ ...prev, pageNumber: page }));
    };

    return (
        <>
            <Row gutter={[8, 8]} style={{ marginTop: 10, marginBottom: 16 }}>
                <Col lg={4} sm={12} xs={24}>
                    <Button.Group style={{ width: '100%' }}>
                        <Button
                            type={viewMode === 'list' ? 'primary' : 'default'}
                            icon={<UnorderedListOutlined />}
                            onClick={() => setViewMode('list')}
                            style={{ flex: 1, height: '32px' }}
                        >
                            {t('Home.Forms.ViewMode.List')}
                        </Button>
                        <Button
                            type={viewMode === 'cards' ? 'primary' : 'default'}
                            icon={<AppstoreOutlined />}
                            onClick={() => setViewMode('cards')}
                            style={{ flex: 1, height: '32px' }}
                        >
                            {t('Home.Forms.ViewMode.Cards')}
                        </Button>
                    </Button.Group>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Input.Search
                        placeholder={t('Home.Forms.SearchPlaceholder')}
                        onSearch={onSearchText}
                        allowClear
                    />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <TreeSelectLocationUser
                        placeholder={t('Location')}
                        onChange={onLocationChange}
                        style={{ width: '100%' }}
                    />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <TreeSelectTopic
                        placeholder={t('List.Topic')}
                        value={filterRequest.topicId}
                        onChange={onTopicChange}
                        style={{ width: '100%' }}
                    />
                </Col>
            </Row>

            {loading ? (
                <Loading />
            ) : listData.length === 0 ? (
                <Empty description={t('Home.Forms.NoFormsFound')} />
            ) : (
                <>
                    {viewMode === 'cards' ? (
                        <Row gutter={[8, 8]}>
                            {listData.map((item) => {
                                return (
                                    <Col xs={24} sm={12} md={8} lg={6} xl={6} key={item.id}>
                                        <Card
                                            className="form-list-item"
                                            style={{
                                                height: "100%",
                                                border: "1px solid #f0f0f0",
                                                borderRadius: 12,
                                                transition: "all 0.3s ease",
                                                backgroundColor: "#fff",
                                                cursor: "pointer",
                                            }}
                                            bodyStyle={{ padding: 20 }}
                                            onClick={() => {
                                                navigate(`/formbuilder/apply/${item.id}`, { state: { redirectURL: "/home" } });
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = "translateY(-2px)";
                                                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.08)";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = "translateY(0)";
                                                e.currentTarget.style.boxShadow = "none";
                                            }}
                                        >
                                            {/* Header com ícone e título */}
                                            <div style={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                marginBottom: 16,
                                                gap: 12
                                            }}>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        width: 40,
                                                        height: 40,
                                                        backgroundColor: "#f8f9fa",
                                                        borderRadius: 8,
                                                        border: "2px solid #e9ecef",
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    <FileTextOutlined style={{ fontSize: 20, color: "#4085c6" }} />
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                                                        <Title
                                                            level={5}
                                                            style={{
                                                                margin: 0,
                                                                marginBottom: 4,
                                                                color: "#1a1a1a",
                                                                fontWeight: 600,
                                                                fontSize: 16,
                                                                lineHeight: 1.3,
                                                                flex: 1,
                                                            }}
                                                            ellipsis={{ rows: 2 }}
                                                        >
                                                            {item.title}
                                                        </Title>
                                                        <div
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleFavoriteForm(item);
                                                            }}
                                                            style={{ cursor: 'pointer', padding: 4, flexShrink: 0 }}
                                                        >
                                                            {item.bookmark ? (
                                                                <StarFilled style={{ fontSize: 16, color: "#facc15" }} />
                                                            ) : (
                                                                <StarTwoTone style={{ fontSize: 16 }} twoToneColor="#facc15" />
                                                            )}
                                                        </div>
                                                    </div>

                                                    {item.version !== undefined && item.version !== null && (
                                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                                                            <Text style={{ fontSize: 11, color: "#666", fontWeight: 500 }}>
                                                                {t('Home.Forms.Version')} {Number(item.version) + 1}
                                                            </Text>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Informações adicionais */}
                                            {item.description && (
                                                <div style={{ marginBottom: 12 }}>
                                                    <Text style={{ fontSize: 12, color: "#666" }} ellipsis>
                                                        {item.description}
                                                    </Text>
                                                </div>
                                            )}

                                            {item.created_at && (
                                                <div>
                                                    <Text style={{ fontSize: 11, color: "#999" }}>
                                                        {t('Home.Forms.CreatedAt')} {item.created_at}
                                                    </Text>
                                                </div>
                                            )}
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    ) : (
                        <List
                            itemLayout="horizontal"
                            dataSource={listData}
                            renderItem={(item) => (
                                <List.Item
                                    style={{
                                        padding: '8px 16px',
                                        marginBottom: '6px',
                                        backgroundColor: '#fff',
                                        border: '1px solid #f0f0f0',
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        boxShadow: 'none'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-2px)";
                                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.08)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = "none";
                                    }}
                                    onClick={() => {
                                        navigate(`/formbuilder/apply/${item.id}`, { state: { redirectURL: "/home" } });
                                    }}
                                    actions={[
                                        <div
                                            key="favorite"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleFavoriteForm(item);
                                            }}
                                            style={{ cursor: 'pointer', padding: 4, flexShrink: 0 }}
                                        >
                                            {item.bookmark ? (
                                                <StarFilled style={{ fontSize: 16, color: "#facc15" }} />
                                            ) : (
                                                <StarTwoTone style={{ fontSize: 16 }} twoToneColor="#facc15" />
                                            )}
                                        </div>
                                    ]}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, width: '100%', flexWrap: 'nowrap' }}>
                                        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 2 }}>
                                                <Title
                                                    level={5}
                                                    style={{
                                                        margin: 0,
                                                        color: "#1a1a1a",
                                                        fontWeight: 600,
                                                        fontSize: 16,
                                                        lineHeight: 1.3,
                                                        flex: '0 1 auto',
                                                        minWidth: 0
                                                    }}
                                                    ellipsis
                                                >
                                                    {item.title}
                                                </Title>
                                                {item.version !== undefined && item.version !== null && (
                                                    <Text style={{ fontSize: 12, color: "#666", fontWeight: 500, flexShrink: 0, whiteSpace: 'nowrap' }}>
                                                        {t('Home.Forms.Version')} {Number(item.version) + 1}
                                                    </Text>
                                                )}
                                            </div>
                                            {item.description && (
                                                <Text
                                                    style={{
                                                        fontSize: 12,
                                                        color: "#666",
                                                        display: 'block',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                    title={item.description}
                                                >
                                                    {item.description}
                                                </Text>
                                            )}
                                        </div>
                                        {item.created_at && (
                                            <Text style={{ fontSize: 11, color: "#999", flexShrink: 0, whiteSpace: 'nowrap' }}>
                                                {item.created_at}
                                            </Text>
                                        )}
                                    </div>
                                </List.Item>
                            )}
                        />
                    )}

                    <Row justify="end" style={{ padding: 10, marginTop: 16 }}>
                        <Pagination
                            current={paginationConfig.current}
                            total={paginationConfig.total}
                            pageSize={filterRequest.pageSize}
                            showSizeChanger={false}
                            onChange={onPageChange}
                        />
                    </Row>
                </>
            )}
        </>
    );
};

export default Forms;
